import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

/**
 * Composable for detecting and managing the current store ID
 * Extracts common store detection logic used across all pages
 */
export function useStoreDetection() {
  const currentStoreId = ref(null)
  const isDetecting = ref(false)

  /**
   * Computed store name from window.app or fallback
   */
  const storeName = computed(() => {
    if (window.app?.selectedStore) {
      const store = window.app.selectedStore
      const match = store.match(/^(.+?)\s*\(\d+\)$/)
      
      return match ? match[1].trim() : store
    }
    
    return currentStoreId.value ? `Store ${currentStoreId.value}` : ''
  })

  /**
   * Detect store ID from various sources in priority order:
   * 1. Already set currentStoreId
   * 2. window.app.storeId
   * 3. window.app.selectedStore (extract from "Store Name (123)")
   * 4. localStorage 'currentStoreId'
   * 5. localStorage 'userData.StoreSelected'
   */
  async function detectStoreId() {
    if (currentStoreId.value) {
      return currentStoreId.value
    }

    isDetecting.value = true

    try {
      // Check window.app.storeId
      if (window.app?.storeId) {
        currentStoreId.value = window.app.storeId
        saveStoreId(currentStoreId.value)
        
        return currentStoreId.value
      }

      // Extract from selectedStore
      if (window.app?.selectedStore) {
        const matches = window.app.selectedStore.match(/\((\d+)\)$/)
        if (matches && matches[1]) {
          currentStoreId.value = matches[1]
          saveStoreId(currentStoreId.value)
          
          return currentStoreId.value
        }
      }

      // Check localStorage
      const savedStoreId = localStorage.getItem('currentStoreId')
      if (savedStoreId) {
        currentStoreId.value = savedStoreId
        
        return currentStoreId.value
      }

      // Try userData in localStorage
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        if (userData.StoreSelected) {
          currentStoreId.value = userData.StoreSelected
          
          return currentStoreId.value
        }
      } catch (e) {
        // Silently ignore parse errors - don't log sensitive data
      }

      return null
    } finally {
      isDetecting.value = false
    }
  }

  /**
   * Save store ID to localStorage
   */
  function saveStoreId(storeId) {
    if (storeId) {
      localStorage.setItem('currentStoreId', storeId)
    }
  }

  /**
   * Handle store change event
   * @param {CustomEvent} event - The store-changed event
   * @param {Function} callback - Optional callback to run after store change
   */
  function handleStoreChange(event, callback) {
    if (event.detail?.storeId) {
      currentStoreId.value = event.detail.storeId
      saveStoreId(currentStoreId.value)
    } else if (event.detail?.storeName) {
      const matches = event.detail.storeName.match(/\((\d+)\)$/)
      if (matches && matches[1]) {
        currentStoreId.value = matches[1]
        saveStoreId(currentStoreId.value)
      }
    }

    if (typeof callback === 'function') {
      callback()
    }
  }

  /**
   * Create a bound handler for the store-changed event
   * @param {Function} callback - Callback to run when store changes
   * @returns {Function} Event handler function
   */
  function createStoreChangeHandler(callback) {
    return event => handleStoreChange(event, callback)
  }

  /**
   * Set up store change listener with automatic cleanup
   * @param {Function} callback - Callback to run when store changes
   * @returns {Function} Cleanup function
   */
  function setupStoreChangeListener(callback) {
    const handler = createStoreChangeHandler(callback)

    window.addEventListener('store-changed', handler)

    // Return cleanup function
    return () => {
      window.removeEventListener('store-changed', handler)
    }
  }

  return {
    currentStoreId,
    storeName,
    isDetecting,
    detectStoreId,
    handleStoreChange,
    createStoreChangeHandler,
    setupStoreChangeListener,
    saveStoreId,
  }
}
